import { get, set, unset } from 'lodash-es';
import { unref } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import {
  cloneDeep,
  dateUtil,
  DAY_FORMAT,
  formatToDate,
  formatToTime,
  isArray,
  isEmpty,
  isFunction,
  isNil,
  isObject,
  isString,
} from '@mts-fe/utils';
import type { FormProps, FormSchemaInner as FormSchema, ModelMappingRule } from '../types/form';

interface UseFormValuesContext {
  defaultValueRef: Ref<any>;
  getSchema: ComputedRef<FormSchema[]>;
  getProps: ComputedRef<FormProps>;
  formModel: Recordable;
}

/**
 * @description deconstruct array-link key. This method will mutate the target.
 */
function tryDeconstructArray(key: string, value: any, target: Recordable) {
  const pattern = /^\[(.+)\]$/;
  if (pattern.test(key)) {
    const match = key.match(pattern);
    if (match && match[1]) {
      const keys = match[1].split(',');
      value = isArray(value) ? value : [value];
      keys.forEach((k, index) => {
        set(target, k.trim(), value[index]);
      });
      return true;
    }
  }
}

/**
 * @description deconstruct object-link key. This method will mutate the target.
 */
function tryDeconstructObject(key: string, value: any, target: Recordable) {
  const pattern = /^\{(.+)\}$/;
  if (pattern.test(key)) {
    const match = key.match(pattern);
    if (match && match[1]) {
      const keys = match[1].split(',');
      value = isObject(value) ? value : {};
      keys.forEach((k) => {
        set(target, k.trim(), value[k.trim()]);
      });
      return true;
    }
  }
}

export function useFormValues({
  defaultValueRef,
  getSchema,
  formModel,
  getProps,
}: UseFormValuesContext) {
  const getFormat = (key: string) => {
    const props = unref(getSchema).find((i) => i.field === key)?.componentProps;
    return props?.valueFormat || props?.format;
  };

  // Processing form values
  function handleFormValues(values: Recordable) {
    if (!isObject(values)) {
      return {};
    }
    const res: Recordable = {};
    for (const item of Object.entries(values)) {
      let [, value] = item;
      const [key] = item;
      if (!key || isFunction(value)) {
        continue;
      }
      const transformDateFunc = unref(getProps).transformDateFunc;
      if (isObject(value)) {
        value = transformDateFunc?.(value, getFormat(key));
      }

      if (isArray(value) && value[0]?.format && value[1]?.format) {
        value = value.map((item) => transformDateFunc?.(item, getFormat(key)));
      }
      // Remove spaces
      if (isString(value)) {
        value = value.trim();
      }
      if (!tryDeconstructArray(key, value, res) && !tryDeconstructObject(key, value, res)) {
        // 没有解构成功的，按原样赋值
        set(res, key, value);
      }
    }
    return handleRangeTimeValue(res);
  }

  /**
   * @description: Processing time interval parameters
   */
  function handleRangeTimeValue(values: Recordable) {
    const fieldMapToTime = unref(getProps).fieldMapToTime;

    if (!fieldMapToTime || !isArray(fieldMapToTime)) {
      return values;
    }

    for (const [field, [startTimeKey, endTimeKey], format = DAY_FORMAT.D] of fieldMapToTime) {
      if (!field || !startTimeKey || !endTimeKey) {
        continue;
      }
      // If the value to be converted is empty, remove the field
      if (!get(values, field)) {
        unset(values, field);
        continue;
      }

      const [startTime, endTime]: string[] = get(values, field);

      const [startTimeFormat, endTimeFormat] = isArray(format) ? format : [format, format];

      if (!isNil(startTime) && !isEmpty(startTime)) {
        set(values, startTimeKey, formatTime(startTime, startTimeFormat));
      }
      if (!isNil(endTime) && !isEmpty(endTime)) {
        set(values, endTimeKey, formatTime(endTime, endTimeFormat));
      }
      unset(values, field);
    }

    return values;
  }

  function formatTime(time: string, format: string) {
    if (format === 'timestamp') {
      return dateUtil(time).unix();
    } else if (format === 'timestampStartDay') {
      return dateUtil(time).startOf('day').unix();
    }
    if (format === 'DT_START') {
      return dateUtil(time).startOf('day').format(DAY_FORMAT.DTS);
    }
    if (format === 'DT_END') {
      return dateUtil(time).endOf('day').format(DAY_FORMAT.DTS);
    }

    return dateUtil(time).format(format);
  }

  function initDefault(itemCallback?: (key: string, value: any, schema: FormSchema) => void) {
    const schemas = unref(getSchema);
    const obj: Recordable = {};
    schemas.forEach((item) => {
      const { defaultValue, defaultValueObj, componentProps = {} } = item;
      const fieldKeys = Object.keys(defaultValueObj || {});
      if (fieldKeys.length) {
        fieldKeys.forEach((field) => {
          obj[field] = defaultValueObj![field];
          if (formModel[field] === undefined) {
            formModel[field] = defaultValueObj![field];
            itemCallback?.(field, obj[field], item);
          }
        });
      }
      if (!isNil(defaultValue)) {
        obj[item.field] = defaultValue;

        if (formModel[item.field] === undefined) {
          formModel[item.field] = defaultValue;
          itemCallback?.(item.field, defaultValue, item);
        }
      }
      if (!isNil(componentProps?.defaultValue)) {
        obj[item.field] = componentProps?.defaultValue;
        if (formModel[item.field] === undefined) {
          formModel[item.field] = componentProps?.defaultValue;
          itemCallback?.(item.field, defaultValue, item);
        }
      }
    });
    defaultValueRef.value = cloneDeep(obj);
  }

  let formMapper: FormModelMapper;
  function updateFormModel(key: string, schema: FormSchema) {
    const { modelMapping } = unref(getProps);
    if (!modelMapping) return;

    if (!formMapper) {
      formMapper = new FormModelMapper(modelMapping);
    }
    formMapper.updateFormModel(formModel, key, schema.componentProps.valueFormat);
  }

  return { handleFormValues, initDefault, updateFormModel };
}

class FormModelMapper {
  private fieldToTargets: Map<string, Array<ModelMappingRule & { target: string }>> = new Map();
  private targetToSources: Map<string, ModelMappingRule> = new Map();

  constructor(modelMapping: Recordable<ModelMappingRule>) {
    Object.entries(modelMapping).forEach(([target, config]) => {
      config.subFields.forEach((field) => {
        !this.fieldToTargets.has(field) && this.fieldToTargets.set(field, []);
        this.fieldToTargets.get(field)!.push({ ...config, target });
      });
      this.targetToSources.set(target, config);
    });
  }

  updateFormModel(formModel: Recordable, key: string, valueFormat?: string) {
    const hasSource = this.targetToSources.has(key);
    if (hasSource) return this.splitMergedField(formModel, key, valueFormat);

    const hasTarget = this.fieldToTargets.has(key);
    if (hasTarget) return this.updateMergedField(formModel, key, valueFormat);
  }

  /** 字段合并（子字段 → 合并字段） */
  updateMergedField(formModel: Recordable, key: string, valueFormat?: string) {
    const targets = this.fieldToTargets.get(key);
    if (!targets) return;

    for (const { target, subFields, separator = ' ' } of targets) {
      let result = '';
      for (let i = 0; i < subFields.length; i++) {
        const field = subFields[i];
        let value = formModel[field] || '';

        if (field === key && value && valueFormat?.includes(':')) {
          value = formatToTime(value);
        }
        if (value && value.includes('-')) {
          value = formatToDate(value, DAY_FORMAT.D);
        }
        result += (i > 0 ? separator : '') + value;
      }

      if (formModel[target] !== result) {
        formModel[target] = result;
      }
    }
  }

  /** 字段拆分（合并字段 → 子字段） */
  splitMergedField(formModel: Recordable, key: string, valueFormat?: string) {
    const config = this.targetToSources.get(key);
    if (!config || !formModel[key]) return;

    const { subFields, separator = ' ' } = config;

    let mergedValue = formModel[key];
    if (mergedValue && valueFormat?.includes(':')) {
      mergedValue = dateUtil(
        mergedValue,
        mergedValue.length <= 16 ? DAY_FORMAT.DT : DAY_FORMAT.DTS,
      ).format(DAY_FORMAT.DTS);
      formModel[key] = mergedValue;
    }

    const parts = String(mergedValue).split(separator);
    if (parts.length !== subFields.length) {
      console.warn(
        `[FormMapper] 拆分失败：${key} 期望 ${subFields.length} 部分，实际 ${parts.length}`,
      );
      return;
    }

    subFields.forEach((field, index) => {
      let value = parts[index];
      if (value && valueFormat) {
        if (value.includes('/') && !value.includes(':')) {
          value = formatToDate(value, DAY_FORMAT._D);
        }
        if (!valueFormat.includes('/') && valueFormat.includes(':')) {
          value = formatToTime(value, DAY_FORMAT.T);
        }
      }

      if (formModel[field] !== value) {
        formModel[field] = value;
      }
    });
  }
}
